<template>
  <div class="my-profile" :class="{ 'is-mobile': isMobile }">
    <div v-if="userDetails?.username" class="my-profile-title">
      <GamIcon :name="GamIconName.SETTINGS_GEAR" :size="GamIconSize.X_LARGE" is-current />
    </div>

    <my-profile-sections />

    <div class="units-section">
      <div v-if="areNotificationsSupported" class="units-row clickable" @click="openNotificationPreferences">
        <div class="units-row-label">
          {{ localize('profile.my.notification.label') }}
        </div>
        <div class="units-row-spacing"></div>
        <GamIcon :name="GamIconName.ARROW_RIGHT" :size="GamIconSize.SMALL" is-current />
      </div>

      <hr v-if="areNotificationsSupported" class="section-divider" />

      <div class="units-row clickable" @click="openEmailPreferences">
        <div class="units-row-label">
          {{ localize('profile.my.email.label') }}
        </div>
        <div class="units-row-spacing"></div>
        <GamIcon :name="GamIconName.ARROW_RIGHT" :size="GamIconSize.SMALL" is-current />
      </div>
    </div>

    <div class="units-section">
      <div class="units-row">
        <div class="units-row-label">
          {{ localize('profile.my.special.units.title') }}
        </div>
        <div class="units-row-spacing"></div>
        <div class="units-row-chips">
          <GamChip
            v-for="(item, index) in unitsOptions"
            :key="`f-item-${index}`"
            ref="filterItem"
            :label="item.label"
            :is-active="userDetails?.unitType === item.value"
            @click="() => changeUnitType(item)"
          />
        </div>
      </div>

      <hr class="section-divider" />

      <div class="units-row color-row">
        <div class="units-row-label">
          {{ localize('profile.my.special.color.title') }}
        </div>
        <div class="units-row-spacing"></div>
        <div class="hue-slider-wrapper">
          <div class="hue-auto-zone" :class="{ 'is-active': isAutoColor }" @click="setAutoColor">
            <GamIcon :name="GamIconName.REFRESH" :size="GamIconSize.SMALL" is-current />
          </div>
          <input
            type="range"
            :class="['hue-slider', { 'is-auto': isAutoColor }]"
            min="0"
            max="359"
            :value="localSliderValue"
            @input="onSliderInput"
            @change="onSliderChange"
          />
        </div>
      </div>
    </div>
    <NotificationPreferencesDrawer :is-open="showNotifications" @close="showNotifications = false" />
    <EmailPreferencesDrawer :is-open="showEmailPrefs" @close="showEmailPrefs = false" />
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { areNotificationsSupported, isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import MyProfileSections from '@/views/main/profile/my-profile/MyProfileSections.vue';
import EmailPreferencesDrawer from '@/views/main/profile/my-profile/sections/EmailPreferences.vue';
import NotificationPreferencesDrawer from '@/views/main/profile/my-profile/sections/NotificationPreferences.vue';
import { storeToRefs } from 'pinia';
import { onBeforeMount, ref, watch } from 'vue';

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const isLoading = ref<boolean>(false);
const showNotifications = ref(false);
const showEmailPrefs = ref(false);

const openNotificationPreferences = () => {
  showNotifications.value = true;
};

const openEmailPreferences = () => {
  showEmailPrefs.value = true;
};

const unitsOptions = [
  { label: 'profile.my.special.units.metric', value: 'metric' },
  { label: 'profile.my.special.units.imperial', value: 'imperial' },
];

const localSliderValue = ref(0);
const isAutoColor = ref(true);

const syncFromStore = () => {
  const hue = userDetails.value?.accentHue;
  if (hue === null || hue === undefined) {
    isAutoColor.value = true;
  } else {
    isAutoColor.value = false;
    localSliderValue.value = hue;
  }
};

watch(() => userDetails.value?.accentHue, syncFromStore, { immediate: true });

const applyHuePreview = (hueAngle: number) => {
  document.documentElement.style.setProperty('--hue', hueAngle.toString());
  document.documentElement.style.setProperty('--hue-2', (hueAngle + 5).toString());
  document.documentElement.style.setProperty('--hue-3', (hueAngle + 10).toString());
  document.documentElement.style.setProperty('--hue-angle', `${hueAngle}deg`);
};

const onSliderInput = (e: Event) => {
  const val = Number((e.target as HTMLInputElement).value);
  localSliderValue.value = val;
  isAutoColor.value = false;
  applyHuePreview(val);
};

const onSliderChange = (e: Event) => {
  const val = Number((e.target as HTMLInputElement).value);
  authStore.updateAccentHue(val);
};

const setAutoColor = async () => {
  isAutoColor.value = true;
  await authStore.updateAccentHue(null);
};

const changeUnitType = async (unitType: any) => {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;

  try {
    await authStore.updateUnitTypePreferences(unitType.value);
  } catch (e) {
    // eslint-disable-next-line
    console.error('Failed to save unit type', e);
  }

  isLoading.value = false;
};

onBeforeMount(async () => {
  if (userDetails) return;

  await authStore.fetchUserInfo();
});
</script>

<style scoped lang="scss">
.my-profile {
  display: flex;
  padding: 0 var(--spacing-xxl);
  gap: var(--spacing-menu);
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  &.is-mobile {
    padding: 0;
  }

  .my-profile-title {
    display: flex;
    align-items: center;
    width: 100%;
    color: var(--color-white-50);
  }

  .units-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding: 0 var(--spacing-l);
    border-radius: var(--radius-small);
    border: 1px solid var(--color-pink);
  }

  .units-row {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    min-height: 68px;
    padding: var(--spacing-l) 0;

    &.clickable {
      cursor: pointer;
    }

    .units-row-label {
      font-size: 14px;
      font-weight: 400;
      letter-spacing: 0.05em;
      color: var(--color-white-50);
    }

    .units-row-spacing {
      flex: 1;
    }

    .units-row-chips {
      display: flex;
      gap: var(--spacing-xs);
    }
  }

  .section-divider {
    width: 100%;
    border: none;
    height: 1px;
    background: var(--color-white-10);
    margin: 0;
  }

  .hue-slider-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    max-width: 220px;
  }

  .hue-auto-zone {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    color: var(--color-white-30);
    transition: color 0.15s;

    &.is-active {
      color: var(--color-white);
    }
  }

  .hue-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 2px;
    border-radius: 1px;
    outline: none;
    cursor: pointer;
    background: linear-gradient(
      to right,
      oklch(0.78 0.24 0),
      oklch(0.78 0.24 60),
      oklch(0.78 0.24 120),
      oklch(0.78 0.24 180),
      oklch(0.78 0.24 240),
      oklch(0.78 0.24 300),
      oklch(0.78 0.24 360)
    );

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: transparent;
      border: 2px solid var(--color-white);
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: transparent;
      border: 2px solid var(--color-white);
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
      cursor: pointer;
    }

    &.is-auto {
      &::-webkit-slider-thumb {
        border-color: var(--color-white-30);
      }

      &::-moz-range-thumb {
        border-color: var(--color-white-30);
      }
    }
  }
}
</style>
